import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { AutoComplete } from 'primereact/autocomplete';

import { RequiredValidation } from 'components/UI/RequiredValidation/RequiredValidation';
import { formatUserDropdown } from 'modules/automation/utils/formatUserDropdown';
import { AssignToItemTemplate } from 'modules/automation/WorkflowBuilder/components';
import { automationModules, contactRoles, leadPositionFilter } from 'constants/automation';
import { useFirmUserList } from 'hooks/useFirmUserList';
import { listContacts } from 'services/Contact/contactServices';
import { generateFullName } from 'utils/generateFullNameUtils';

const PositionFilter = ({ index }) => {
  const {
    formState: { errors },
    control,
    watch,
    resetField,
  } = useFormContext();
  const { userList, isFirmUserLoading } = useFirmUserList(false, false, true, true);
  const [filteredClients, setFilteredClients] = useState(null);

  const trigger = watch('trigger');
  const is_lead = watch('is_lead_automation') || (trigger?.module === automationModules.LEADS && trigger?.code !== 'TRIG_LEAD_CONV_CASE');
  const role = watch(`filters.${index}.role`);

  const isContactRole = contactRoles.includes(role);

  const searchClient = async (event) => {
    let obj = { pageLimit: 30, keyWord: event.query, contactType: '' };
    let response = await listContacts(obj);
    if (response?.data?.contacts?.length > 0) {
      let data = [];
      response.data.contacts.forEach((element) => {
        data.push({
          label: element.contact_type === 'person' ? generateFullName(element) : element.title,
          value: element.contact_id,
        });
      });
      setFilteredClients(data);
    } else {
      setFilteredClients([]);
    }
  };

  return (
    <>
      <div className="me-2 filterbar-condition">
        {is_lead ? (
          <Controller
            name={`filters.${index}.role`}
            control={control}
            rules={{ required: 'Role is required' }}
            render={({ field }) => (
              <Dropdown
                value={field.value || []}
                name={field.name}
                options={leadPositionFilter}
                onChange={(e) => {
                  resetField(`filters.${index}.value`, { defaultValue: [] });
                  field.onChange(e.value);
                }}
                filter
                className="w-100 input-shadow "
                placeholder="Select"
                itemTemplate={AssignToItemTemplate}
                inputRef={field.ref}
              />
            )}
          />
        ) : (
          <Controller
            name={`filters.${index}.role`}
            control={control}
            rules={{ required: 'Role is required' }}
            render={({ field }) => (
              <Dropdown
                value={field.value || []}
                name={field.name}
                options={
                  formatUserDropdown({
                    users: [],
                    hideInactive: true,
                    includeContacts: false,
                    includesUserGroups: false,
                    includePrimarySecondary: true,
                  }) || []
                }
                onChange={(e) => {
                  resetField(`filters.${index}.value`, { defaultValue: [] });
                  field.onChange(e.value);
                }}
                filter
                className="w-100 input-shadow "
                placeholder="Select"
                display="chip"
                optionLabel="label"
                optionGroupLabel="groupLabel"
                optionGroupChildren="items"
                itemTemplate={AssignToItemTemplate}
                inputRef={field.ref}
              />
            )}
          />
        )}
        <RequiredValidation field={`filters.${index}.role`} errors={errors} />
      </div>

      <div className="me-2 filterbar-value">
        {isContactRole ? (
          <Controller
            name={`filters.${index}.value`}
            control={control}
            render={({ field }) => (
              <AutoComplete
                multiple
                placeholder="Enter contact name to search"
                style={{ width: '100%' }}
                inputStyle={{ width: '100%' }}
                value={field.value}
                selectedItemTemplate={(option) => option?.label}
                suggestions={filteredClients}
                completeMethod={searchClient}
                field="label"
                onChange={(e) => field.onChange(e.value)}
                className="flex-grow-1 input-shadow"
              />
            )}
          />
        ) : (
          <Controller
            name={`filters.${index}.value`}
            control={control}
            rules={{ required: 'Value is required' }}
            render={({ field }) => (
              <MultiSelect
                value={field.value || []}
                name={field.name}
                options={
                  formatUserDropdown({
                    users: userList,
                    hideInactive: true,
                    includeContacts: false,
                    includesUserGroups: false,
                    includePrimarySecondary: false,
                    includesTeamMembers: false,
                    includeSelectedInactiveUser: true,
                    selectedItems: field.value || [],
                  }) || []
                }
                onChange={(e) => field.onChange(e.value)}
                filter
                className="w-100 input-shadow "
                placeholder="Select"
                display="chip"
                optionLabel="label"
                optionGroupLabel="groupLabel"
                optionGroupChildren="items"
                itemTemplate={AssignToItemTemplate}
                dropdownIcon={isFirmUserLoading ? 'pi pi-spin pi-spinner' : undefined}
              />
            )}
          />
        )}
        <RequiredValidation field={`filters.${index}.value`} errors={errors} />
      </div>
    </>
  );
};

export default PositionFilter;
